/***************************************************************************************
 * FileName      : SimulationOverlayService.ts
 * Description   : 시뮬레이션 작동 시 3D 공간에 떠 있는 값 오버레이 텍스트 박스 관리
 *                 - scenario 카드에서 시뮬레이션 재생 시, 매칭되는 장비 위에
 *                   "{key}: {value}" 텍스트 박스를 mp.quadTextPlane 으로 배치
 *                 - 위치/회전/크기는 assets/json/simulation-overlay/{equipmentId}.json 에서 로드
 *                   (matterport_mvp1/13-simulation-overlay-placement 편집기로 저장한 데이터)
 *                 - 저장된 위치 없는 키는 hotspot anchor 기반으로 fallback 계산
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.05.14
 * Modified Date : 2026.05.14 사용자 편집한 overlay JSON 위치 적용
 * Reference     : matterport_mvp1/src/13-simulation-overlay-placement (위치 편집기),
 *                 HotspotTagService(fallback), QuadTextPlacementManager
 ***************************************************************************************/

import { HotspotTagService, HotspotTagData } from './HotspotTagService';
import { QuadTextPlacementManager, getQuadTextPlacementManager } from './QuadTextPlacementManager';
import { computeLabelCorners, QuadCorners, Vector3 } from '../types/quad-text';
import { MonitoringData } from '../types/MonitoringData';

// 2026.05.14 : 박은정 : 시뮬레이션 전용 라벨 ID 접두사 (monitor 라벨과 네임스페이스 분리)
const LABEL_ID_PREFIX = 'sim-overlay-';

// 2026.05.14 : 박은정 : fallback 시 사용할 라벨 시각 기본값
const DEFAULT_LABEL_WIDTH = 0.32;
const DEFAULT_LABEL_HEIGHT = 0.045;
const DEFAULT_STACK_GAP = 0.008;

// 2026.05.14 : 박은정 : 텍스트 렌더 설정 (편집기와 동일)
const FONT_SIZE = 72;
const TEXT_COLOR = '#ffffff';
const BG_COLOR = 'rgba(15, 23, 42, 0.85)';
const TEXTURE = { w: 1024, h: 144 };

// 2026.05.14 : 박은정 : 값 변경 시 시각적 강조 (노란 배경 + 검정 글씨, 일정 시간 후 원복)
const HIGHLIGHT_TEXT_COLOR = '#0f172a';
const HIGHLIGHT_BG_COLOR = 'rgba(250, 204, 21, 0.95)'; // amber-400
const HIGHLIGHT_DURATION_MS = 500;

function makeTextStyle(textColor: string, backgroundColor: string) {
  return {
    fontSize: FONT_SIZE,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    textColor,
    backgroundColor,
    textAlign: 'center' as const,
    lineHeight: 1.2,
    padding: 12,
  };
}

// 2026.05.14 : 박은정 : overlay JSON 항목 — 13-simulation-overlay-placement 편집기 출력 포맷과 일치
interface OverlayLabelConfig {
  key: string;
  deviceId: string;
  position: Vector3;
  yaw: number;
  width: number;
  height: number;
}

/**
 * 2026.05.14 : 박은정 : 시뮬레이션 시 장비 위 텍스트 박스 오버레이 서비스
 *   - ensureLabels 가 매 tick 호출돼도 안전 (lazy 생성 + idempotent update)
 *   - 장비 전환 / 카드 전환 / 시나리오 변경 시 clear() 호출 필요
 *   - 우선 overlay JSON 의 사용자 편집 위치 사용, 없으면 hotspot anchor 기반 자동 배치
 */
export class SimulationOverlayService {
  private static instance: SimulationOverlayService;
  private quadTextManager: QuadTextPlacementManager = getQuadTextPlacementManager();
  private hotspotService: HotspotTagService = HotspotTagService.getInstance();
  // 현재 등록된 라벨: key → 정보
  private placedLabels: Map<string, { labelId: string; deviceId: string; stackIndex: number }> = new Map();
  // 비동기 addText 동시 호출 방지
  private setupInProgress: boolean = false;
  // 현재 라벨이 속한 장비 ID (장비 전환 감지용)
  private currentEquipmentId: string | null = null;
  // 2026.05.14 : 박은정 : 디바이스 필터 (구성요소 카드 부품 클릭 시 해당 디바이스만 표시)
  private currentDeviceFilter: string | null = null;
  // overlay JSON 캐시: equipmentId → (key → 설정)
  private overlayCache: Map<string, Map<string, OverlayLabelConfig>> = new Map();
  // overlay JSON 로딩 in-flight Promise (중복 fetch 방지)
  private overlayLoading: Map<string, Promise<Map<string, OverlayLabelConfig>>> = new Map();
  // 2026.05.14 : 박은정 : 값 변경 강조 — 이전 값 추적 + 원복 타이머
  private previousValues: Map<string, string> = new Map();
  private highlightTimers: Map<string, any> = new Map();

  static getInstance(): SimulationOverlayService {
    if (!SimulationOverlayService.instance) {
      SimulationOverlayService.instance = new SimulationOverlayService();
    }
    return SimulationOverlayService.instance;
  }

  /**
   * 2026.05.14 : 박은정 : scenario key 에서 deviceId 추출.
   *   예) PP-102-FREQ → PP-102 / TIT-105-PV → TIT-105 / BF-304-RUN → BF-304
   */
  private extractDeviceId(scenarioKey: string): string {
    const lastDash = scenarioKey.lastIndexOf('-');
    if (lastDash <= 0) return scenarioKey;
    return scenarioKey.substring(0, lastDash);
  }

  /**
   * 2026.05.14 : 박은정 : 장비별 overlay JSON 로드 (캐시 + 중복 fetch 방지).
   *   파일 없거나 파싱 실패 시 빈 Map 반환.
   */
  private async loadOverlay(equipmentId: string): Promise<Map<string, OverlayLabelConfig>> {
    if (this.overlayCache.has(equipmentId)) {
      return this.overlayCache.get(equipmentId)!;
    }
    if (this.overlayLoading.has(equipmentId)) {
      return this.overlayLoading.get(equipmentId)!;
    }

    const promise = (async () => {
      try {
        const r = await fetch(`./assets/json/simulation-overlay/${equipmentId}.json`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const arr: OverlayLabelConfig[] = await r.json();
        const map = new Map<string, OverlayLabelConfig>();
        for (const item of arr) {
          if (item && item.key) map.set(item.key, item);
        }
        this.overlayCache.set(equipmentId, map);
        return map;
      } catch (e) {
        // 파일 없음/파싱 실패 → 빈 Map 캐시 (fallback 사용)
        const empty = new Map<string, OverlayLabelConfig>();
        this.overlayCache.set(equipmentId, empty);
        return empty;
      } finally {
        this.overlayLoading.delete(equipmentId);
      }
    })();
    this.overlayLoading.set(equipmentId, promise);
    return promise;
  }

  /**
   * 2026.05.14 : 박은정 : overlay JSON 설정으로부터 4꼭짓점 corners 계산
   */
  private cornersFromOverlay(cfg: OverlayLabelConfig): QuadCorners {
    return computeLabelCorners(
      cfg.position,
      { x: 0, y: 0, z: 0 },
      cfg.width,
      cfg.height,
      { x: 0, y: cfg.yaw, z: 0 },
    );
  }

  /**
   * 2026.05.14 : 박은정 : (fallback) hotspot anchor + stemVector 기반 corners 계산.
   *   overlay JSON 에 없는 키 — 추후 시나리오 추가로 등장한 신규 키 등에 사용.
   */
  private cornersFromHotspot(tagData: HotspotTagData, stackIndex: number): QuadCorners {
    const sv = tagData.stemVector;
    const stemLen = Math.hypot(sv.x, sv.y, sv.z) || 1;
    const unit = { x: sv.x / stemLen, y: sv.y / stemLen, z: sv.z / stemLen };
    const stemH = tagData.stemHeight || 0.5;
    const tip = {
      x: tagData.anchorPosition.x + unit.x * stemH,
      y: tagData.anchorPosition.y + unit.y * stemH + stackIndex * (DEFAULT_LABEL_HEIGHT + DEFAULT_STACK_GAP),
      z: tagData.anchorPosition.z + unit.z * stemH,
    };
    return computeLabelCorners(
      tip,
      { x: 0, y: 0, z: 0 },
      DEFAULT_LABEL_WIDTH,
      DEFAULT_LABEL_HEIGHT,
      { x: 0, y: 0, z: 0 },
    );
  }

  /**
   * 2026.05.14 : 박은정 : 시나리오 파일에서 사용되는 모든 키를 미리 추출해 라벨을 일괄 배치.
   *   재생 시작 전(시나리오 선택 시점) 호출 → 시뮬레이션 클릭 즉시 모든 필드가 보임.
   *   ensureLabels 와 달리 데이터 tick 을 기다리지 않음. 값은 "—" placeholder.
   *
   *   호출 흐름:
   *     Main.handleScenarioSelect(equipmentId, scenarioId)
   *       → simulationOverlayService.prepareScenarioLabels(equipmentId, scenarioId)
   */
  async prepareScenarioLabels(equipmentId: string, scenarioId: string): Promise<void> {
    if (!this.quadTextManager.isInitialized()) return;

    // 장비 변경 감지 → 이전 라벨 정리
    if (this.currentEquipmentId !== null && this.currentEquipmentId !== equipmentId) {
      await this.clear();
    }
    this.currentEquipmentId = equipmentId;

    // overlay JSON 로드 (위치 정보)
    const overlay = await this.loadOverlay(equipmentId);

    // 시나리오 파일에서 모든 키 수집 (initialState + phase timeline)
    let allKeys: Set<string>;
    try {
      const r = await fetch(`./assets/json/scenario/${scenarioId}.json`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const sc: any = await r.json();
      allKeys = new Set<string>();
      for (const k of Object.keys(sc.initialState?.controls ?? {})) allKeys.add(k);
      for (const k of Object.keys(sc.initialState?.sensors ?? {})) allKeys.add(k);
      for (const p of sc.phases ?? []) {
        for (const ev of p.timeline ?? []) {
          for (const k of Object.keys(ev.set ?? {})) allKeys.add(k);
        }
      }
    } catch (e) {
      console.warn('[SimulationOverlayService] 시나리오 로드 실패:', scenarioId, e);
      return;
    }

    if (this.setupInProgress) return;
    this.setupInProgress = true;
    try {
      const newKeys = [...allKeys].filter(k => !this.placedLabels.has(k));
      newKeys.sort();

      const existingStack = new Map<string, number>();
      for (const info of this.placedLabels.values()) {
        existingStack.set(info.deviceId, Math.max(existingStack.get(info.deviceId) ?? 0, info.stackIndex + 1));
      }

      for (const key of newKeys) {
        const deviceId = this.extractDeviceId(key);
        const cfg = overlay.get(key);

        let corners: QuadCorners;
        let stackIndex = existingStack.get(deviceId) ?? 0;

        if (cfg) {
          corners = this.cornersFromOverlay(cfg);
        } else {
          const tagData = this.hotspotService.getTagData(equipmentId, deviceId);
          if (!tagData) continue;
          corners = this.cornersFromHotspot(tagData, stackIndex);
          existingStack.set(deviceId, stackIndex + 1);
        }

        const labelId = `${LABEL_ID_PREFIX}${equipmentId}-${key}`;
        try {
          await this.quadTextManager.addText({
            id: labelId,
            name: key,
            text: `${key}: —`,
            textStyle: makeTextStyle(TEXT_COLOR, BG_COLOR),
            corners,
            opacity: 1.0,
            visible: true,
            textureSize: TEXTURE,
            showBorder: false,
          });
          this.placedLabels.set(key, { labelId, deviceId, stackIndex });
        } catch (e) {
          console.warn('[SimulationOverlayService] addText 실패:', key, e);
        }
      }
    } finally {
      this.setupInProgress = false;
    }
  }

  /**
   * 2026.05.14 : 박은정 : 특정 deviceId 에 매칭되는 라벨만 배치.
   *   구성요소 카드에서 부품 클릭 시 해당 장비의 키만 노출하려는 용도.
   *   - 이전에 표시되던 다른 디바이스 라벨은 자동 제거
   *   - 데이터에 없는 키는 placeholder "—" 로 표시 (deviceId 만으로도 보이게)
   */
  async ensureLabelsForDevice(equipmentId: string, deviceId: string, data: MonitoringData | null): Promise<void> {
    if (!this.quadTextManager.isInitialized()) return;

    // 장비 변경 또는 다른 디바이스 → 이전 라벨 전부 제거
    if (this.currentEquipmentId !== equipmentId || this.currentDeviceFilter !== deviceId) {
      await this.clear();
      this.currentEquipmentId = equipmentId;
      this.currentDeviceFilter = deviceId;
    }

    if (this.setupInProgress) return;
    this.setupInProgress = true;
    try {
      const overlay = await this.loadOverlay(equipmentId);

      // 이 deviceId 에 속한 모든 키 후보:
      //   1) overlay JSON 에 정의된 키 중 deviceId 매칭
      //   2) data 에 등장한 키 중 deviceId 매칭 (overlay 에 없을 수도)
      const keys = new Set<string>();
      for (const [k] of overlay.entries()) {
        if (this.extractDeviceId(k) === deviceId) keys.add(k);
      }
      if (data) {
        for (const k of Object.keys(data.sensorValues ?? {})) {
          if (this.extractDeviceId(k) === deviceId) keys.add(k);
        }
        for (const k of Object.keys(data.controlValues ?? {})) {
          if (this.extractDeviceId(k) === deviceId) keys.add(k);
        }
      }

      const sortedKeys = [...keys].sort();
      const existingStack = new Map<string, number>();

      for (const key of sortedKeys) {
        if (this.placedLabels.has(key)) continue; // 중복 방지
        const cfg = overlay.get(key);

        let corners: QuadCorners;
        let stackIndex = existingStack.get(deviceId) ?? 0;

        if (cfg) {
          corners = this.cornersFromOverlay(cfg);
        } else {
          const tagData = this.hotspotService.getTagData(equipmentId, deviceId);
          if (!tagData) continue;
          corners = this.cornersFromHotspot(tagData, stackIndex);
          existingStack.set(deviceId, stackIndex + 1);
        }

        const labelId = `${LABEL_ID_PREFIX}${equipmentId}-${key}`;
        const value = data ? this.formatValue(key, data) : null;
        try {
          await this.quadTextManager.addText({
            id: labelId,
            name: key,
            text: `${key}: ${value ?? '—'}`,
            textStyle: makeTextStyle(TEXT_COLOR, BG_COLOR),
            corners,
            opacity: 1.0,
            visible: true,
            textureSize: TEXTURE,
            showBorder: false,
          });
          this.placedLabels.set(key, { labelId, deviceId, stackIndex });
        } catch (e) {
          console.warn('[SimulationOverlayService] addText 실패:', key, e);
        }
      }

      if (data) this.updateValues(data);
    } finally {
      this.setupInProgress = false;
    }
  }

  /**
   * 2026.05.14 : 박은정 : 현재 모니터링 데이터에 등장한 모든 키에 대해 라벨을 보장.
   *   - overlay JSON 의 사용자 편집 위치 우선
   *   - 없으면 hotspot fallback (새로 추가된 키 안전망)
   *   - 매 tick 호출돼도 누락 키만 추가하고 값만 갱신
   */
  async ensureLabels(equipmentId: string, data: MonitoringData): Promise<void> {
    if (!this.quadTextManager.isInitialized()) return;

    // 장비 변경 감지 → 이전 라벨 모두 제거 후 재구성
    if (this.currentEquipmentId !== null && this.currentEquipmentId !== equipmentId) {
      await this.clear();
    }
    this.currentEquipmentId = equipmentId;

    if (this.setupInProgress) {
      this.updateValues(data);
      return;
    }

    this.setupInProgress = true;
    try {
      const overlay = await this.loadOverlay(equipmentId);

      const allKeys = new Set<string>([
        ...Object.keys(data.sensorValues ?? {}),
        ...Object.keys(data.controlValues ?? {}),
      ]);

      const newKeys = [...allKeys].filter(k => !this.placedLabels.has(k));

      if (newKeys.length > 0) {
        // 기존 라벨 스택 카운트 (fallback 용)
        const existingStack = new Map<string, number>();
        for (const info of this.placedLabels.values()) {
          existingStack.set(info.deviceId, Math.max(existingStack.get(info.deviceId) ?? 0, info.stackIndex + 1));
        }

        // 안정적 순서 (장비 키 정렬)
        newKeys.sort();

        for (const key of newKeys) {
          const deviceId = this.extractDeviceId(key);
          const cfg = overlay.get(key);

          let corners: QuadCorners;
          let stackIndex = existingStack.get(deviceId) ?? 0;

          if (cfg) {
            // 사용자 편집 위치 사용 (stack 무시 — 편집기에서 이미 분리해 둠)
            corners = this.cornersFromOverlay(cfg);
          } else {
            // overlay JSON 에 없는 키 → hotspot fallback
            const tagData = this.hotspotService.getTagData(equipmentId, deviceId);
            if (!tagData) {
              // hotspot 도 없는 키 → 표시 불가, 스킵
              continue;
            }
            corners = this.cornersFromHotspot(tagData, stackIndex);
            existingStack.set(deviceId, stackIndex + 1);
          }

          const labelId = `${LABEL_ID_PREFIX}${equipmentId}-${key}`;
          const currentValue = this.formatValue(key, data);

          try {
            await this.quadTextManager.addText({
              id: labelId,
              name: key,
              text: `${key}: ${currentValue ?? '—'}`,
              textStyle: {
                fontSize: FONT_SIZE,
                fontFamily: 'Arial, sans-serif',
                fontWeight: 'bold',
                textColor: TEXT_COLOR,
                backgroundColor: BG_COLOR,
                textAlign: 'center',
                lineHeight: 1.2,
                padding: 12,
              },
              corners,
              opacity: 1.0,
              visible: true,
              textureSize: TEXTURE,
              showBorder: false,
            });
            this.placedLabels.set(key, { labelId, deviceId, stackIndex });
          } catch (e) {
            console.warn('[SimulationOverlayService] addText 실패:', key, e);
          }
        }
      }

      // 모든 기존 라벨 값 갱신
      this.updateValues(data);
    } finally {
      this.setupInProgress = false;
    }
  }

  /**
   * 2026.05.14 : 박은정 : 현재 등록된 라벨들의 값을 데이터에서 찾아 갱신.
   *   값이 이전과 달라지면 일정 시간 동안 강조 색상 적용 (amber bg + dark text).
   */
  updateValues(data: MonitoringData): void {
    if (!this.quadTextManager.isInitialized()) return;

    for (const [key, info] of this.placedLabels.entries()) {
      const formatted = this.formatValue(key, data);
      if (formatted === null) continue;

      const prev = this.previousValues.get(key);
      const changed = prev !== undefined && prev !== formatted;
      this.previousValues.set(key, formatted);

      const labelText = `${key}: ${formatted}`;

      if (changed) {
        // 강조 색상 + 텍스트 갱신
        this.quadTextManager.updateText(info.labelId, {
          text: labelText,
          visible: true,
          textStyle: makeTextStyle(HIGHLIGHT_TEXT_COLOR, HIGHLIGHT_BG_COLOR),
        });
        // 기존 원복 타이머가 있으면 취소 (연속 변경 시 강조 시간 리셋)
        const existing = this.highlightTimers.get(key);
        if (existing) clearTimeout(existing);
        // 일정 시간 후 원래 색상으로 원복
        const timer = setTimeout(() => {
          this.quadTextManager.updateText(info.labelId, {
            textStyle: makeTextStyle(TEXT_COLOR, BG_COLOR),
          });
          this.highlightTimers.delete(key);
        }, HIGHLIGHT_DURATION_MS);
        this.highlightTimers.set(key, timer);
      } else {
        this.quadTextManager.updateText(info.labelId, {
          text: labelText,
          visible: true,
        });
      }
    }
  }

  /**
   * 2026.05.14 : 박은정 : data 에서 key 값을 찾아 표시용 문자열로 반환.
   *   우선순위: controlValues → sensorValues. 없으면 null.
   */
  private formatValue(key: string, data: MonitoringData): string | null {
    const c = data.controlValues?.[key];
    if (c !== undefined) return String(c);
    const s = data.sensorValues?.[key];
    if (s !== undefined) return String(s);
    return null;
  }

  /**
   * 2026.05.14 : 박은정 : 모든 시뮬레이션 오버레이 라벨 제거.
   */
  async clear(): Promise<void> {
    // 2026.05.14 : 박은정 : 강조 타이머도 모두 취소 (메모리 누수 / 시나리오 전환 시 잔류 방지)
    for (const t of this.highlightTimers.values()) clearTimeout(t);
    this.highlightTimers.clear();
    this.previousValues.clear();

    if (!this.quadTextManager.isInitialized()) {
      this.placedLabels.clear();
      this.currentEquipmentId = null;
      this.currentDeviceFilter = null;
      return;
    }
    for (const info of this.placedLabels.values()) {
      try {
        await this.quadTextManager.removeText(info.labelId);
      } catch (e) {
        // 제거 실패 무시
      }
    }
    this.placedLabels.clear();
    this.currentEquipmentId = null;
    this.currentDeviceFilter = null;
  }
}
